import {
  Loader2,
  Mail,
  User,
  Building,
  CreditCard,
  Calendar,
  CheckCircle,
} from "lucide-react";
import { useState, useEffect } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/lib/supabase";

interface UserProfileProps {
  onLogout?: () => void;
}

export function UserProfile({ onLogout }: UserProfileProps) {
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    avatar_url: user?.avatar_url || "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const isDevMode = import.meta.env.VITE_DEV_MODE === "true";

      if (isDevMode) {
        // In dev mode, use mock user data
        setUser({
          id: "dev-user-1",
          email: "user@example.com",
          full_name: "Test User",
          avatar_url: "",
          created_at: new Date().toISOString(),
          role: "user",
          organization_id: null,
          subscription: {
            type: "free",
            status: "active",
          },
        });
      } else {
        // Production: get user from Supabase
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();
        if (authUser) {
          // Fetch full user profile
          const { data: profile } = await supabase
            .from("user_profiles")
            .select("*")
            .eq("id", authUser.id)
            .single();

          setUser({
            ...authUser,
            ...profile,
            full_name:
              profile?.full_name || authUser.user_metadata?.full_name || "User",
            avatar_url:
              profile?.avatar_url || authUser.user_metadata?.avatar_url || "",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      setErrorMessage("Failed to load user profile");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Cookie-based auth: send the session_id cookie via credentials: 'include'.
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ""}/api/user-profiles/${user?.id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update profile");
      }

      setSuccessMessage("Profile updated successfully");
      setEditing(false);
      // Refresh user data
      await fetchUser();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to update profile",
      );
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <Alert variant="destructive">
        <AlertDescription>User not found. Please log in.</AlertDescription>
      </Alert>
    );
  }

  const initials = user.full_name
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase();

  return (
    <div className="space-y-6">
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>
                    Manage your personal information and account details
                  </CardDescription>
                </div>
                <Avatar className="h-20 w-20">
                  <AvatarImage src={user.avatar_url} alt={user.full_name} />
                  <AvatarFallback className="text-2xl">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {successMessage && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    {successMessage}
                  </AlertDescription>
                </Alert>
              )}

              {errorMessage && (
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="full_name"
                    type="text"
                    className="pl-10"
                    value={editing ? formData.full_name : user.full_name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        full_name: e.target.value,
                      }))
                    }
                    disabled={!editing || saving}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    className="pl-10"
                    value={user.email}
                    disabled
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed
                </p>
              </div>

              {user.organization_id && (
                <div className="space-y-2">
                  <Label htmlFor="organization">Organization</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="organization"
                      type="text"
                      className="pl-10"
                      value={user.organization_id}
                      disabled
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Role</Label>
                <Badge variant="secondary" className="text-sm">
                  {user.role || "User"}
                </Badge>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end space-x-2">
              {editing ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditing(false);
                      setFormData({
                        full_name: user.full_name,
                        email: user.email,
                        avatar_url: user.avatar_url || "",
                      });
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </>
              ) : (
                <Button onClick={() => setEditing(true)}>Edit Profile</Button>
              )}
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="subscription">
          <Card>
            <CardHeader>
              <CardTitle>Subscription Details</CardTitle>
              <CardDescription>
                Manage your subscription plan and billing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Current Plan</span>
                  </div>
                  <p className="text-2xl font-bold">
                    {user.subscription?.type || "Free"}
                  </p>
                  {user.subscription?.status && (
                    <Badge
                      variant={
                        user.subscription.status === "active"
                          ? "default"
                          : "secondary"
                      }
                    >
                      {user.subscription.status}
                    </Badge>
                  )}
                </div>
                {user.subscription?.current_period_end && (
                  <div className="text-right space-y-1">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>Next billing date</span>
                    </div>
                    <p className="font-medium">
                      {new Date(
                        user.subscription.current_period_end,
                      ).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              {(!user.subscription || user.subscription.type === "free") && (
                <Alert>
                  <AlertDescription>
                    Upgrade to a premium plan to access advanced features and
                    tools.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline">View Billing History</Button>
              <Button>
                {user.subscription?.type === "free"
                  ? "Upgrade Plan"
                  : "Manage Subscription"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Account Settings</CardTitle>
              <CardDescription>
                Manage your account preferences and security settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Two-Factor Authentication</p>
                    <p className="text-sm text-muted-foreground">
                      Add an extra layer of security to your account
                    </p>
                  </div>
                  <Button variant="outline">Enable</Button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-muted-foreground">
                      Manage your email notification preferences
                    </p>
                  </div>
                  <Button variant="outline">Configure</Button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">API Keys</p>
                    <p className="text-sm text-muted-foreground">
                      Manage API keys for third-party integrations
                    </p>
                  </div>
                  <Button variant="outline">Manage</Button>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between border-t pt-6">
              <div>
                <p className="text-sm text-muted-foreground">
                  Account created on{" "}
                  {new Date(user.created_at || Date.now()).toLocaleDateString()}
                </p>
              </div>
              <div className="space-x-2">
                {onLogout && (
                  <Button variant="outline" onClick={onLogout}>
                    Log Out
                  </Button>
                )}
                <Button variant="destructive">Delete Account</Button>
              </div>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
